import { parseRoomsQuery } from '../cityListShared';

export const pad2 = (n: number) => n.toString().padStart(2, '0');

export const localYMD = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export const toLocalStartYMD = (value?: string): string => {
  if (!value) {
    return localYMD(new Date());
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  const d = new Date(value);

  if (Number.isNaN(d.getTime())) {
    return localYMD(new Date());
  }

  return localYMD(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
};

export const addDaysYMD = (ymd: string, days: number): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return localYMD(dt);
};

export const normalizeStringId = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const result = String(value).trim();

  return result.length > 0 ? result : null;
};

export const normalizePositiveNumber = (value: unknown): number => {
  const result = Number(value);

  return Number.isFinite(result) && result > 0 ? result : 0;
};

export const sameExternalId = (left: unknown, right: unknown): boolean => {
  const a = normalizeStringId(left);
  const b = normalizeStringId(right);

  if (!a || !b) {
    return false;
  }

  return a.toLowerCase() === b.toLowerCase();
};

export const getRoomTypeId = (room: any): string | null => {
  if (!room) {
    return null;
  }

  const candidates = [
    room.roomTypeId,
    room.RoomTypeId,
    room.room_type_id,
    room.roomTypeID,
  ];

  for (const candidate of candidates) {
    const id = normalizeStringId(candidate);

    if (id) {
      return id;
    }
  }

  return null;
};

export const getRatePlanId = (room: any): string | null => {
  if (!room) {
    return null;
  }

  return normalizeStringId(room.ratePlanId ?? room.RatePlanId ?? room.rate_plan_id);
};

export const getRoomsRemaining = (room: any): number | null => {
  if (!room) {
    return null;
  }

  const candidates = [
    room.roomsRemaining,
    room.RoomsRemaining,
    room.remainingRooms,
    room.RemainingRooms,
    room.rooms_remaining,
  ];

  for (const candidate of candidates) {
    if (candidate === null || candidate === undefined || candidate === '') {
      continue;
    }

    const value = Number(candidate);

    if (Number.isFinite(value)) {
      return value;
    }
  }

  return null;
};

export const getInternalRoomId = (room: any): number => {
  if (!room) {
    return 0;
  }

  const candidates = [
    room.internalRoomId,
    room.InternalRoomId,
    room.id,
    room.Id,
    room.localRoomId,
    room.local_room_id,
  ];

  for (const candidate of candidates) {
    const id = normalizePositiveNumber(candidate);

    if (id > 0) {
      return id;
    }
  }

  return 0;
};

export const getApiRoomId = (room: any): string | null => {
  if (!room) {
    return null;
  }

  const roomTypeId = getRoomTypeId(room);

  if (roomTypeId) {
    return roomTypeId;
  }

  const candidates = [
    room.apiRoomId,
    room.ApiRoomId,
    room.apiRoomID,
    room.ApiRoomID,
    room.api_room_id,
    room.externalRoomId,
    room.external_room_id,
    room.externalId,
    room.ExternalId,
  ];

  for (const candidate of candidates) {
    const id = normalizeStringId(candidate);

    if (id) {
      return id;
    }
  }

  return null;
};

export const normalizeOriginalRoom = (room: any) => {
  if (!room) {
    return {
      id: 0,
      internalRoomId: 0,
      apiRoomId: null,
      ApiRoomId: null,
      title: '',
      capacity: 0,
      roomsRemaining: 0,
      roomsRemainingPerDay: [],
      ratePlans: [],
      ratePlanId: null,
      ratePlanTitle: null,
      displayPrice: 0,
      isActive: false,
      media: [],
      rawRoom: null,
    };
  }

  const internalRoomId = getInternalRoomId(room);
  const apiRoomId = getApiRoomId(room);

  return {
    ...room,
    id: internalRoomId,
    internalRoomId: internalRoomId,
    apiRoomId: apiRoomId,
    ApiRoomId: apiRoomId,
    title: room.title ?? room.Title ?? room.roomTitle ?? room.RoomTitle ?? '',
    capacity: room.capacity ?? room.Capacity ?? 0,
    roomsRemaining:
      room.roomsRemaining ??
      room.RoomsRemaining ??
      room.remainingRooms ??
      room.RemainingRooms ??
      0,
    roomsRemainingPerDay:
      room.roomsRemainingPerDay ??
      room.RoomsRemainingPerDay ??
      room.rooms_remaining_per_day ??
      [],
    ratePlans: room.ratePlans ?? room.RatePlans ?? [],
    ratePlanId: room.ratePlanId ?? room.RatePlanId ?? room.rate_plan_id ?? null,
    ratePlanTitle: room.ratePlanTitle ?? room.RatePlanTitle ?? room.rate_plan_title ?? null,
    displayPrice:
      room.displayPrice ??
      room.DisplayPrice ??
      room.display_price ??
      room.price ??
      0,
    isActive: room.isActive ?? room.IsActive ?? true,
    media: room.media ?? room.Media ?? [],
    rawRoom: room,
  };
};

export const mapLocalRooms = (originalRooms: any[]) =>
  originalRooms.map((originalRoom: any) => ({
    ...originalRoom,
    id: normalizePositiveNumber(originalRoom.internalRoomId ?? originalRoom.id),
    internalRoomId: normalizePositiveNumber(
      originalRoom.internalRoomId ?? originalRoom.id,
    ),
    apiRoomId: normalizeStringId(originalRoom.apiRoomId ?? originalRoom.ApiRoomId),
    ApiRoomId: normalizeStringId(originalRoom.apiRoomId ?? originalRoom.ApiRoomId),
  }));

export const findOriginalRoom = (
  localRooms: any[],
  apiRoomId: string | null,
  internalRoomId: number,
): any | null => {
  if (apiRoomId) {
    const apiMatch = localRooms.find((room: any) =>
      sameExternalId(room.apiRoomId ?? room.ApiRoomId ?? getRoomTypeId(room), apiRoomId),
    );

    if (apiMatch) {
      return apiMatch;
    }
  }

  if (internalRoomId > 0) {
    const internalMatch = localRooms.find(
      (room: any) =>
        normalizePositiveNumber(room.internalRoomId ?? room.id) === internalRoomId,
    );

    if (internalMatch) {
      return internalMatch;
    }
  }

  return null;
};

export function prepareHotelFromDetailsPayload(result: any) {
  const {
    accommodation,
    distances = [],
    media = [],
    room = [],
    notes = [],
  } = result.data;

  const features = [
    ...(result.data.features || []),
    ...(result.data.features2 || []),
    ...(result.data.features3 || []),
    ...(result.data.features4 || []),
  ];

  const checkInTime =
    features.find((feature: any) => feature.keyValue === 'ROF')?.value || 'نامشخص';

  const checkOutTime =
    features.find((feature: any) => feature.keyValue === 'RE')?.value || 'نامشخص';

  const roomsAvailable =
    features.find((feature: any) => feature.keyValue === 'NOR')?.value || '0';

  const floors = features.find((feature: any) => feature.keyValue === 'NOF')?.value || '0';

  const hotelRules = features
    .filter((feature: any) => feature.type === 4)
    .map((feature: any) => ({
      featureName: feature.featureName,
      value: feature.value,
    }));

  const originalRooms = (Array.isArray(room) ? room : []).map((rawRoom: any) =>
    normalizeOriginalRoom(rawRoom),
  );

  const localRooms = mapLocalRooms(originalRooms);

  const apiAccommodationId = normalizeStringId(
    accommodation.apiAccommodationId ??
      accommodation.ApiAccommodationId ??
      accommodation.apiAccommodationID ??
      accommodation.id,
  );

  const prepared = {
    id: accommodation.id,
    ApiAccommodationId: apiAccommodationId,
    apiAccommodationId: apiAccommodationId,
    name: accommodation.name,
    stars: accommodation.starRating,
    address: accommodation.address,
    description: accommodation.description,
    mainImage: media.find((m: any) => m.mediaType === 'Image')?.mediaURLs?.[0] || '',
    gallery: media
      .filter((m: any) => m.mediaType === 'Image')
      .flatMap((m: any) => m.mediaURLs || []),
    discount: accommodation.discountPercentage
      ? `${accommodation.discountPercentage}٪`
      : undefined,
    distances: (distances || []).map((d: any) => ({
      place: d.locationName,
      walkingDistance: d.distanceOnFoot,
      walkingTime: d.distanceMinutesOnFoot,
      busDistance: d.distanceByBus,
      busTime: d.distanceMinutesByBus,
      carDistance: d.distanceByCar,
      carTime: d.distanceMinutesByCar,
    })),
    hotelRules,
    specialAmenities: features
      .filter((feature: any) => feature.type === 2)
      .map((feature: any) => feature.featureName),
    hotelFacilities: features
      .filter((feature: any) => feature.type === 3)
      .map((feature: any) => feature.featureName),
    checkInTime,
    checkOutTime,
    roomsAvailable,
    floors,
    locationEmbed: accommodation.locationEmbed || '',
    latitude: accommodation.latitude,
    longitude: accommodation.longitude,
    cityID: accommodation.zoneID,
    cityNameFa: accommodation.cityName || '',
    notes: notes || [],
    rooms: originalRooms,
    phone: accommodation.phone,
    whatsapp: accommodation.whatsapp,
    onlineBookingUrl: accommodation.onlineBookingUrl,
    zoneID: accommodation.zoneID,
    Accommodation: accommodation,
  };

  return { prepared, localRooms };
}

export function normalizeAvailabilityRooms(roomsAvail: any[], localRooms: any[]) {
  return roomsAvail.map((rawRoom: any, index: number) => {
    const roomTypeId = getRoomTypeId(rawRoom) ?? getApiRoomId(rawRoom);
    const ratePlanId = getRatePlanId(rawRoom);

    let originalRoom = roomTypeId ? findOriginalRoom(localRooms, roomTypeId, 0) : null;

    if (originalRoom) {
      const originalApiRoomId = normalizeStringId(
        originalRoom.apiRoomId ?? originalRoom.ApiRoomId ?? getRoomTypeId(originalRoom),
      );

      if (roomTypeId && originalApiRoomId && !sameExternalId(originalApiRoomId, roomTypeId)) {
        originalRoom = null;
      }
    }

    if (!originalRoom && !roomTypeId) {
      const explicitInternalRoomId = normalizePositiveNumber(
        rawRoom?.internalRoomId ??
          rawRoom?.InternalRoomId ??
          rawRoom?.localRoomId ??
          rawRoom?.local_room_id,
      );

      if (explicitInternalRoomId > 0) {
        originalRoom = findOriginalRoom(localRooms, null, explicitInternalRoomId);
      }
    }

    const finalApiRoomId =
      roomTypeId ??
      normalizeStringId(originalRoom?.apiRoomId ?? originalRoom?.ApiRoomId ?? null);

    const finalInternalRoomId =
      originalRoom &&
      (!finalApiRoomId ||
        sameExternalId(
          originalRoom.apiRoomId ?? originalRoom.ApiRoomId ?? getRoomTypeId(originalRoom),
          finalApiRoomId,
        ))
        ? normalizePositiveNumber(originalRoom.internalRoomId ?? originalRoom.id)
        : 0;

    const roomsRemaining = getRoomsRemaining(rawRoom);
    const displayPrice =
      rawRoom?.displayPrice ??
      rawRoom?.DisplayPrice ??
      rawRoom?.display_price ??
      rawRoom?.price ??
      rawRoom?.Price ??
      0;

    return {
      ...rawRoom,
      id: finalInternalRoomId,
      internalRoomId: finalInternalRoomId,
      roomTypeId: finalApiRoomId,
      room_type_id: finalApiRoomId,
      apiRoomId: finalApiRoomId,
      ApiRoomId: finalApiRoomId,
      title:
        rawRoom?.title ??
        rawRoom?.Title ??
        rawRoom?.roomTitle ??
        rawRoom?.RoomTitle ??
        originalRoom?.title ??
        '',
      capacity: rawRoom?.capacity ?? rawRoom?.Capacity ?? originalRoom?.capacity ?? 0,
      roomsRemaining: roomsRemaining ?? 0,
      remainingRooms: roomsRemaining ?? rawRoom?.remainingRooms ?? rawRoom?.RemainingRooms ?? 0,
      roomsRemainingPerDay:
        rawRoom?.roomsRemainingPerDay ??
        rawRoom?.RoomsRemainingPerDay ??
        rawRoom?.rooms_remaining_per_day ??
        originalRoom?.roomsRemainingPerDay ??
        [],
      ratePlanId,
      ratePlanTitle:
        rawRoom?.ratePlanTitle ??
        rawRoom?.RatePlanTitle ??
        rawRoom?.rate_plan_title ??
        null,
      price: rawRoom?.price ?? rawRoom?.Price ?? displayPrice,
      displayPrice,
      isActive: rawRoom?.isActive ?? rawRoom?.IsActive ?? true,
      media: rawRoom?.media ?? rawRoom?.Media ?? originalRoom?.media ?? [],
      ratePlans: rawRoom?.ratePlans ?? rawRoom?.RatePlans ?? [],
      __availabilityIndex: index,
    };
  });
}

export function parseRequestedRooms(roomsParam?: string | null) {
  const parsedRooms = parseRoomsQuery(roomsParam);
  const totals = parsedRooms.reduce(
    (acc, room) => {
      acc.totalAdults += room.adults;
      acc.totalChildren += room.children.length;
      acc.totalRoomsCount += 1;
      return acc;
    },
    { totalAdults: 0, totalChildren: 0, totalRoomsCount: 0 },
  );

  return { parsedRooms, ...totals };
}
